<template>
  <div>
    <header class="vm-page-header">
      <div>
        <h1>Detalhe da consulta</h1>
        <span v-if="transaction" class="vm-page-header__sub">
          {{ transaction.patient || "Consulta" }} ·
          {{ formatDateFull(transaction.transaction_date) }}
        </span>
        <span v-else class="vm-page-header__sub">Consulta</span>
      </div>
      <div class="vm-page-header__actions">
        <router-link
          :to="{ name: 'FinanceiroDashboard' }"
          class="btn btn--accent btn--sm"
        >
          Ver repasse →
        </router-link>
      </div>
    </header>

    <div class="fin-body">
      <router-link :to="{ name: 'FinanceiroExtrato' }" class="backlink">
        ‹ Voltar ao extrato
      </router-link>

      <!-- Skeleton -->
      <div v-if="isLoading" class="fdc-grid">
        <div class="card" style="padding:var(--space-6)">
          <div v-for="i in 4" :key="i" style="margin-bottom:var(--space-4)">
            <div class="vm-skel" style="width:100%;height:18px" />
          </div>
        </div>
        <div class="card" style="padding:var(--space-6)">
          <div v-for="i in 3" :key="i" style="margin-bottom:var(--space-4)">
            <div class="vm-skel" style="width:100%;height:18px" />
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-else-if="!transaction" class="card" style="padding:var(--space-6)">
        <div class="vm-empty">
          <div class="vm-empty-ill">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M12 8v4M12 16h.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h3>Movimentação não encontrada</h3>
          <p>A movimentação solicitada não está disponível.</p>
          <router-link
            :to="{ name: 'FinanceiroExtrato' }"
            class="btn btn--outline"
            style="margin-top:var(--space-4)"
          >
            Voltar ao extrato
          </router-link>
        </div>
      </div>

      <template v-else>
        <div class="fdc-grid">
          <!-- Coluna esquerda: Dados da consulta -->
          <div class="card" style="padding:var(--space-6)">
            <div
              class="row"
              style="gap:var(--space-4);padding-bottom:var(--space-5);border-bottom:1px solid var(--color-gray-100)"
            >
              <div class="avatar avatar--lg">{{ initials }}</div>
              <div class="stack" style="gap:2px;flex:1">
                <span
                  style="font-size:var(--font-size-lg);font-weight:var(--fw-bold);color:var(--color-gray-900)"
                >
                  {{ transaction.patient || "Paciente" }}
                </span>
                <span
                  v-if="transaction.patient_cpf"
                  style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
                >
                  CPF {{ transaction.patient_cpf }}
                </span>
                <span
                  style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
                >
                  Teleconsulta · Online
                </span>
              </div>
              <span
                class="badge"
                :class="
                  transaction.type === 'crédito'
                    ? 'badge--success'
                    : 'badge--neutral'
                "
              >
                {{
                  transaction.type === "crédito"
                    ? "Pagamento confirmado"
                    : "Débito"
                }}
              </span>
            </div>

            <dl class="dl" style="margin-top:var(--space-4)">
              <div>
                <dt>Data</dt>
                <dd class="num">
                  {{ formatDateShort(transaction.transaction_date) }}
                </dd>
              </div>
              <div>
                <dt>Horário</dt>
                <dd class="num">—</dd>
              </div>
              <div>
                <dt>Modalidade</dt>
                <dd>Teleconsulta (online)</dd>
              </div>
              <div>
                <dt>Duração</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>Método de pagamento</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>Pago em</dt>
                <dd class="num">
                  {{ formatDateShort(transaction.transaction_date) }}
                </dd>
              </div>
            </dl>

            <div
              class="row"
              style="gap:var(--space-2);margin-top:var(--space-5);flex-wrap:wrap"
            >
              <span class="badge badge--neutral badge--no-dot">
                Sem documentos
              </span>
            </div>
          </div>

          <!-- Coluna direita: Composição do valor -->
          <div class="card" style="padding:var(--space-6)">
            <span class="fieldset-title">Composição do valor</span>
            <div class="breakdown" style="margin-top:var(--space-3)">
              <div class="br">
                Valor bruto da consulta
                <b class="money">
                  {{
                    transaction.type === "crédito"
                      ? formatBRL(transaction.amount / 100)
                      : "—"
                  }}
                </b>
              </div>
              <div class="br">
                Taxa da plataforma ({{ platformPercent }}%)
                <b class="money" style="color:var(--color-danger)">
                  {{
                    transaction.type === "crédito"
                      ? "− " +
                        formatBRL((transaction.amount / 100) * platformFraction)
                      : "—"
                  }}
                </b>
              </div>
              <div class="br br--total">
                Seu líquido
                <b class="money money--pos">
                  {{
                    transaction.type === "crédito"
                      ? formatBRL((transaction.amount / 100) * doctorFraction)
                      : "—"
                  }}
                </b>
              </div>
            </div>

            <!-- Card de status -->
            <div
              class="card"
              style="background:var(--color-gray-50);border:1px solid var(--color-gray-200);padding:var(--space-4);margin-top:var(--space-5)"
            >
              <div
                class="row"
                style="justify-content:space-between;margin-bottom:var(--space-3)"
              >
                <span
                  style="font-size:var(--font-size-sm);color:var(--color-gray-600)"
                >
                  Status do pagamento
                </span>
                <span
                  class="badge"
                  :class="
                    transaction.type === 'crédito'
                      ? 'badge--success'
                      : 'badge--neutral'
                  "
                >
                  {{ transaction.type === "crédito" ? "Pago" : "—" }}
                </span>
              </div>
              <div class="row" style="justify-content:space-between">
                <span
                  style="font-size:var(--font-size-sm);color:var(--color-gray-600)"
                >
                  Status do repasse
                </span>
                <span class="badge badge--warning">Agendado</span>
              </div>
            </div>

            <router-link
              :to="{ name: 'FinanceiroDashboard' }"
              class="btn btn--outline btn--block"
              style="margin-top:var(--space-4);text-decoration:none"
            >
              Ver repasse →
            </router-link>
          </div>
        </div>

        <!-- Linha do tempo financeira -->
        <div class="card" style="padding:var(--space-6)">
          <span class="fieldset-title">Linha do tempo financeira</span>
          <div class="stack" style="gap:0;margin-top:var(--space-3)">
            <div
              class="row"
              style="gap:var(--space-3);padding:var(--space-3) 0;border-bottom:1px solid var(--color-gray-100)"
            >
              <span class="badge badge--success badge--no-dot fdc-tl-icon">
                ✓
              </span>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Pagamento confirmado
                </span>
                <span
                  class="num"
                  style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                >
                  {{ formatDateShort(transaction.transaction_date) }}
                </span>
              </div>
              <span class="money">
                {{
                  transaction.amount != null
                    ? formatBRL(transaction.amount / 100)
                    : "—"
                }}
              </span>
            </div>

            <div
              class="row"
              style="gap:var(--space-3);padding:var(--space-3) 0;border-bottom:1px solid var(--color-gray-100)"
            >
              <span class="badge badge--success badge--no-dot fdc-tl-icon">
                ✓
              </span>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Consulta realizada
                </span>
                <span
                  class="num"
                  style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                >
                  {{ formatDateShort(transaction.transaction_date) }}
                </span>
              </div>
            </div>

            <div
              class="row"
              style="gap:var(--space-3);padding:var(--space-3) 0"
            >
              <span class="badge badge--warning badge--no-dot fdc-tl-icon">
                →
              </span>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Repasse agendado
                </span>
                <span
                  class="num"
                  style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                >
                  previsto próximo ciclo
                </span>
              </div>
              <span class="money money--pos">
                {{
                  transaction.type === "crédito"
                    ? formatBRL((transaction.amount / 100) * doctorFraction)
                    : "—"
                }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { pt as locale } from "date-fns/locale";
import { Doctor } from "../services/doctor";

export default {
  name: "FinanceiroConsulta",

  data() {
    return {
      comissionPercent: 80
    };
  },

  async mounted() {
    if (!this.$store.state.doctor.financials?.transactions?.length) {
      await this.$store.dispatch("doctor/getFinancials");
    }
    Doctor.earnings()
      .then(res => {
        this.comissionPercent = res.data.comission_percent;
      })
      .catch(() => {});
  },

  methods: {
    parseAmount(str) {
      if (!str) return 0;
      const cleaned = String(str)
        .replace(/[R$\s]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      return parseFloat(cleaned) || 0;
    },
    formatBRL(val) {
      if (!val) return "R$ 0,00";
      const str = val.toFixed(2).replace(".", ",");
      const parts = str.split(",");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return "R$ " + parts.join(",");
    },
    formatDateShort(date) {
      if (!date) return "—";
      try {
        return format(parseISO(date), "dd/MM/yyyy");
      } catch {
        return date;
      }
    },
    formatDateFull(date) {
      if (!date) return "";
      try {
        return format(parseISO(date), "d 'de' MMMM 'de' yyyy", { locale });
      } catch {
        return date;
      }
    }
  },

  computed: {
    doctorFraction() {
      return this.comissionPercent / 100;
    },
    platformFraction() {
      return 1 - this.comissionPercent / 100;
    },
    platformPercent() {
      return 100 - this.comissionPercent;
    },
    isLoading() {
      return this.$store.getters["doctor/loading"];
    },
    transactions() {
      return this.$store.state.doctor.financials?.transactions || [];
    },
    transaction() {
      const id = this.$route.params.id;
      if (!id) return null;
      return this.transactions[parseInt(id)] || null;
    },
    initials() {
      const name = this.transaction?.patient || "";
      return name.substring(0, 2).toUpperCase() || "?";
    }
  }
};
</script>

<style lang="scss" scoped>
.fin-body {
  padding: var(--space-6) var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 980px;
  width: 100%;
}

.fdc-grid {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.fdc-tl-icon {
  width: 24px;
  height: 24px;
  justify-content: center;
  padding: 0;
  flex: none;
}

.dl {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  > div {
    display: flex;
    justify-content: space-between;
  }

  dt {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
  }

  dd {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
    margin: 0;
  }
}
</style>
